import { type Country } from '@resurs-internal/mui/assets/countries';
import CryptoJS from 'crypto-js';

import {
  type TokenByGrantRequest,
  type TokenRevocationRequest,
} from '@/types/oba-auth-service';

export const baseUrl = `${window.location.protocol}//${window.location.host}`;
const redirectUri = `${window.location.protocol}//${window.location.host}/`;

const authConfig = {
  authUrl: `${import.meta.env.VITE_RESURS_EKS_HOST}/api/oauth2/auth/`,
  client_id: import.meta.env.VITE_AUTH_CLIENT_ID,
  refreshUrl: `${import.meta.env.VITE_RESURS_EKS_HOST}/api/oauth2/refresh_token/`,
  revokeUrl: `${import.meta.env.VITE_RESURS_EKS_HOST}/api/oauth2/token/revoke/`,
  scope: 'consumer-deposits-application-',
  tokenUrl: `${import.meta.env.VITE_RESURS_EKS_HOST}/api/oauth2/token/`,
};

export const createPKCE = () => {
  const randomBytes = CryptoJS.lib.WordArray.random(32);
  const codeVerifier = CryptoJS.enc.Base64.stringify(randomBytes)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
  const codeChallenge = CryptoJS.SHA256(codeVerifier)
    .toString(CryptoJS.enc.Base64)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');

  return {
    codeChallenge,
    codeMethod: 'S256',
    codeVerifier,
  };
};

const PRESERVED_AUTH_PARAMS = ['campaignCode', 'configurationRuleId'];

/**
 * The path to return to after (re)authenticating, keeping the parameters the
 * application needs, e.g. the selected fixed term.
 */
export const authReturnPath = (
  pathname: string,
  searchParams: URLSearchParams,
): string => {
  const params = new URLSearchParams();

  PRESERVED_AUTH_PARAMS.forEach((key) => {
    const value = searchParams.get(key);

    if (value) params.set(key, value);
  });

  const query = params.toString();

  return query ? `${pathname}?${query}` : pathname;
};

export const authRedirectUrl = (
  path: string,
  state: string,
  country: Country,
) => {
  const { codeChallenge, codeMethod, codeVerifier } = createPKCE();

  sessionStorage.setItem('auth_state', state);
  sessionStorage.setItem('code_verifier', codeVerifier);

  const fullRedirectUri = `${redirectUri}${path}`;
  const encodedRedirectUri = encodeURIComponent(fullRedirectUri);

  return `${authConfig.authUrl}?redirect_uri=${encodedRedirectUri}&response_type=code&code_challenge=${codeChallenge}&code_challenge_method=${codeMethod}&client_id=${authConfig.client_id}&scope=${authConfig.scope}${country.valueOf()}&state=${state}`;
};

export const parseTokenRequestWithCode = (
  code: string,
  codeVerifier: string,
): TokenByGrantRequest => {
  return {
    client_id: authConfig.client_id,
    code,
    code_verifier: codeVerifier,
    grant_type: 'authorization_code',
    redirect_uri: redirectUri,
    scope: authConfig.scope,
  };
};

export const parseTokenRequestWithRefreshToken = (
  refresh_token: string,
): TokenByGrantRequest => {
  return {
    client_id: authConfig.client_id,
    grant_type: 'refresh_token',
    redirect_uri: redirectUri,
    refresh_token,
    scope: authConfig.scope,
  };
};

export const parseRevokeRefreshTokenRequest = (
  token: string,
): TokenRevocationRequest => {
  return {
    token,
    tokenTypeHint: 'refresh_token',
  };
};

export const parseRevokeAccessTokenRequest = (
  token: string,
): TokenRevocationRequest => {
  return {
    token,
    tokenTypeHint: 'access_token',
  };
};
export const authUrl = authConfig.authUrl;
export const refreshUrl = authConfig.refreshUrl;
export const revokeUrl = authConfig.revokeUrl;
export const tokenUrl = authConfig.tokenUrl;
